import {
  AngebotsId,
  RabattStufeLabel,
  SelectedZusatzoption,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { sortByCustomAppOrder } from '@ps20-frontend/reducers';
import { IAppState } from '../appState';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { angebotIsTarifiert, hasUnderwriterInfo } from './warenkorb.selector';

export interface ProduktForTabelle extends AngebotsId {
  produkt: WarenkorbAngebot;
  appId: App;
  rbdInfo: string;
  tbInfo: string;
  vrInfo: string;
  kombirabatt: RabattStufeLabel;
}

const extremwetterschutzId = 'weitereNaturgefahren';
const weitereProdukte = [App.GEGE, App.JHP]; // order matters

export const selectAusgewaehlteProdukteForTabelle = (
  state: IAppState,
): Array<ProduktForTabelle> => {
  const unsortedAngebote = state.warenkorb.map(produkt => {
    return {
      produkt,
      appId: produkt.appId,
      rbdInfo: getRbdString(produkt),
      tbInfo: getTbString(produkt),
      vrInfo: getVrString(produkt),
      kombirabatt: getRabattstufe(produkt),
    } as ProduktForTabelle;
  });
  return moveWeitereProdukteToEndOfList(unsortedAngebote);
};

function moveWeitereProdukteToEndOfList(
  unsortedAngebote: Array<ProduktForTabelle>,
): Array<ProduktForTabelle> {
  let appOrder: Array<App> = [...new Set(unsortedAngebote.map(angebot => angebot.produkt.appId))];
  const includesWeitereProdukte: App[] = weitereProdukte.filter(app => appOrder.includes(app));
  appOrder = [
    ...appOrder.filter(app => !weitereProdukte.includes(app)),
    ...includesWeitereProdukte,
  ];

  return sortByCustomAppOrder(appOrder, unsortedAngebote);
}

export const selectHasAnyProduktUnderwriterInfo = (state: IAppState): boolean => {
  return selectAusgewaehlteProdukteForTabelle(state)?.some(
    produktForTabelle => produktForTabelle?.produkt && hasUnderwriterInfo(produktForTabelle.produkt),
  );
};

export const selectHasAnyProduktNoUnderwriterInfo = (state: IAppState): boolean => {
  return selectAusgewaehlteProdukteForTabelle(state)?.some(
    produktForTabelle => !produktForTabelle?.produkt || !hasUnderwriterInfo(produktForTabelle.produkt),
  );
};

export function getRbdString(produkt: WarenkorbAngebot): string {
  if (!angebotIsTarifiert(produkt)) {
    return '';
  } else if ((produkt.rbd_max == null || produkt.rbd_max === 0) && !produkt.beibehalten) {
    return 'RB: -';
  } else if ((produkt.rbd == null || produkt.rbd === 0) && !produkt.beibehalten) {
    return 'RB: 0';
  } else {
    return produkt.rbd ? `RB: ${produkt.rbd}` : 'RB: 0';
  }
}

export function getTbString(produkt: WarenkorbAngebot): string {
  if (!angebotIsTarifiert(produkt) || produkt.treuebonus == null || produkt.treuebonus === 0) {
    return '';
  } else if (produkt.treuebonus) {
    return `TB: ${produkt.treuebonus}`;
  }
}

export function getVrString(produkt: WarenkorbAngebot): string {
  if (!angebotIsTarifiert(produkt)) {
    return '';
  } else if (
    (produkt.vorteilsrabatt == null || produkt.vorteilsrabatt === 0) &&
    !produkt.beibehalten
  ) {
    return 'VR: 0';
  } else {
    return produkt.vorteilsrabatt ? `VR: ${produkt.vorteilsrabatt}` : 'VR: 0';
  }
}

export function getRabattstufe(produkt: WarenkorbAngebot): RabattStufeLabel {
  if (angebotIsTarifiert(produkt)) {
    switch (Math.min(produkt.rabattstufe, 5)) {
      case 0:
        return produkt.isZaehlstueck ? RabattStufeLabel.Z : RabattStufeLabel.NULL;
      case 1:
        return RabattStufeLabel.NULL;
      case 2:
        return RabattStufeLabel.ZWEI;
      case 3:
        return RabattStufeLabel.DREI;
      case 4:
        return RabattStufeLabel.VIER;
      case 5:
        return RabattStufeLabel.FUENF;
    }
  } else {
    return RabattStufeLabel.LEER;
  }
}

export const selectProdukteForAngebot = (state: IAppState): Array<WarenkorbAngebot> =>
  state.warenkorb.filter(produkt => angebotIsTarifiert(produkt) && produkt.bruttopreis > 0);

export const selectDoppeltVersicherteZusatzoptionen = (state: IAppState): Array<string> => {
  const addressToProductsMap = createAddressToProdukteMap(state);

  let doppeltVersicherteOptionen: string[] = [];
  addressToProductsMap.forEach((produkte: WarenkorbAngebot[]) => {
    if (produkte.length > 1) {
      const doppelt = findDoppeltVersicherteOptionen(produkte);
      doppeltVersicherteOptionen = doppeltVersicherteOptionen.concat(doppelt);
    }
  });
  return filterDoppeltVersicherteOptionen(doppeltVersicherteOptionen);
};

function createAddressToProdukteMap(state: IAppState): Map<string, WarenkorbAngebot[]> {
  const addressToProductsMap = new Map<string, WarenkorbAngebot[]>();
  selectProdukteForAngebot(state).forEach((produkt: WarenkorbAngebot) => {
    const produkteForRisiko = addressToProductsMap.get(produkt.versichertesRisiko);
    if (produkteForRisiko && !produkt.appId.includes(App.TKV)) {
      produkteForRisiko.push(produkt);
    } else {
      addressToProductsMap.set(produkt.versichertesRisiko, [produkt]);
    }
  });
  return addressToProductsMap;
}

function findDoppeltVersicherteOptionen(produkte: WarenkorbAngebot[]): string[] {
  const inclusive: string[] = [];
  const notInclusive: string[] = [];
  const doppelt: string[] = [];
  produkte.forEach((produkt: WarenkorbAngebot) => {
    produkt.selectedZusatzoptionen.forEach((option: SelectedZusatzoption) => {
      const id = option.id;
      if (option.isInclusive) {
        if (notInclusive.includes(id)) {
          doppelt.push(id);
        }
        inclusive.push(id);
      } else {
        if (notInclusive.includes(id) || inclusive.includes(id)) {
          doppelt.push(id);
        }
        notInclusive.push(id);
      }
    });
  });
  return doppelt;
}

function filterDoppeltVersicherteOptionen(doppeltVersicherteOptionen: string[]): string[] {
  doppeltVersicherteOptionen = doppeltVersicherteOptionen.filter(
    (option: string) => option !== extremwetterschutzId,
  );
  return Array.from(new Set(doppeltVersicherteOptionen));
}
